import { useCallback, useEffect, useState } from "react";

export type Dimensions = { width: number; height: number };

const useRefDimensions = (ref) => {
  const [dimensions, setDimensions] = useState<Dimensions>();
  const measureElement = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDimensions({ width: rect.width, height: rect.height });
    }
  }, [ref]);

  useEffect(() => {
    if (!ref.current) return;

    // Initial measurement - use double requestAnimationFrame to ensure layout is complete
    // This is needed because flex layouts can take multiple frames to settle
    const measureAfterLayout = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          measureElement();
        });
      });
    };
    measureAfterLayout();

    // Use ResizeObserver to detect element size changes (including flex layout changes)
    // Check if ResizeObserver is available (not available in test environments like jsdom)
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver((entries) => {
        // Check if we have a valid size before measuring
        for (const entry of entries) {
          if (entry.contentRect.height > 0 || entry.contentRect.width > 0) {
            // Use requestAnimationFrame to batch measurements
            requestAnimationFrame(() => {
              measureElement();
            });
            break;
          }
        }
      });

      resizeObserver.observe(ref.current);
    }

    // Also listen to window resize as fallback
    const handleResize = () => {
      measureElement();
    };

    window.addEventListener("resize", handleResize);

    // Additional fallback: measure after a short delay to catch late layout changes
    const timeoutId = setTimeout(() => {
      measureElement();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [ref, measureElement]);

  return dimensions;
};

export default useRefDimensions;
